import { stageHasWaiting } from './taskProgress.js';

export const PRIORITIES = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'urgent', label: 'Urgent' },
];

export const WAITING_ON = [
  { id: 'client', label: 'Client' },
  { id: 'team_member', label: 'Team member' },
  { id: 'approval', label: 'Approval' },
  { id: 'files', label: 'Files or content' },
  { id: 'external', label: 'External party' },
  { id: 'other', label: 'Other' },
  { id: 'blocked', label: 'Blocked' },
];

export const TASK_KINDS = [
  { id: 'team', label: 'Team task' },
  { id: 'client_request', label: 'Client request' },
];

export const QUICK_FILTERS = [
  { id: 'overdue', label: 'Overdue' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

export const PROJECT_ROLES = [
  { id: 'project_lead', label: 'Project lead' },
  { id: 'project_manager', label: 'Project manager' },
  { id: 'designer', label: 'Designer' },
  { id: 'developer', label: 'Developer' },
  { id: 'seo', label: 'SEO' },
  { id: 'social_media', label: 'Social media' },
  { id: 'admin', label: 'Admin' },
  { id: 'other', label: 'Other' },
];

export const URGENCY_WINDOW_DAYS = 7;

export function dueIso(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

export function todayIso() {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function addDaysIso(iso, days) {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${nextMonth}-${nextDay}`;
}

export function hasStarted(task, today = todayIso()) {
  const start = dueIso(task?.start_date);
  return !start || start <= today;
}

export function endOfWeekIso(iso = todayIso()) {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const weekday = date.getDay();
  const delta = weekday === 0 ? 0 : 7 - weekday;
  date.setDate(date.getDate() + delta);
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${nextMonth}-${nextDay}`;
}

export function taskKind(task) {
  if (task?.kind === 'client_request' || task?.kind === 'team') return task.kind;
  return task?.is_client_task ? 'client_request' : 'team';
}

export function taskIsComplete(task, columns = []) {
  const column = columns.find((item) => (item.key || item.id) === task?.status);
  if (column) return Boolean(column.marks_complete);
  return task?.status === 'done' || Boolean(task?.completed_at);
}

export function isOverdue(task, columns, today = todayIso()) {
  const due = dueIso(task?.due_date);
  return Boolean(due) && due < today && hasStarted(task, today) && task?.status !== 'cancelled' && !taskIsComplete(task, columns);
}

export function isDueToday(task, columns, today = todayIso()) {
  return dueIso(task?.due_date) === today && hasStarted(task, today) && task?.status !== 'cancelled' && !taskIsComplete(task, columns);
}

export function isDueThisWeek(task, columns, today = todayIso()) {
  const due = dueIso(task?.due_date);
  const end = addDaysIso(today, URGENCY_WINDOW_DAYS);
  return Boolean(due) && due > today && due <= end && hasStarted(task, today) && task?.status !== 'cancelled' && !taskIsComplete(task, columns);
}

export function taskBucket(task, columns, today = todayIso()) {
  if (task?.status === 'cancelled') return 'cancelled';
  if (taskIsComplete(task, columns)) return 'completed';
  if (!hasStarted(task, today)) return 'upcoming';
  if (isOverdue(task, columns, today)) return 'overdue';
  if (isDueToday(task, columns, today)) return 'today';
  if (isDueThisWeek(task, columns, today)) return 'week';
  if (stageHasWaiting(columns, task?.status)) return 'waiting';
  return 'upcoming';
}

export function waitingLabel(value) {
  return WAITING_ON.find((item) => item.id === value)?.label || '';
}

export function waitingDetail(task) {
  if (task?.waiting_on === 'team_member' && task?.waiting_user?.name) {
    return task.waiting_user.name;
  }
  return waitingLabel(task?.waiting_on);
}

export function priorityLabel(value) {
  return PRIORITIES.find((item) => item.id === value)?.label || 'Medium';
}

export function visibleQueueTasks(tasks, filters, columns) {
  const today = todayIso();
  return tasks.filter((task) => {
    const clientKey = task.client?.id || task.client_id || task.project?.client?.id || task.project?.client_id || '';
    if (filters.clientId && String(clientKey) !== String(filters.clientId)) return false;
    if (filters.projectId === 'none' && task.project_id) return false;
    if (filters.projectId && filters.projectId !== 'none' && String(task.project_id) !== String(filters.projectId)) return false;
    if (filters.assigneeId === 'none' && task.assigned_to) return false;
    if (filters.assigneeId && filters.assigneeId !== 'none' && String(task.assigned_to || '') !== String(filters.assigneeId)) return false;
    if (filters.priority && (task.priority || 'medium') !== filters.priority) return false;
    if (filters.quick === 'overdue') return taskBucket(task, columns, today) === 'overdue';
    if (filters.quick === 'today') return taskBucket(task, columns, today) === 'today';
    if (filters.quick === 'week') return taskBucket(task, columns, today) === 'week';
    if (filters.quick === 'upcoming') return taskBucket(task, columns, today) === 'upcoming';
    if (filters.quick === 'waiting') return stageHasWaiting(columns, task.status);
    if (filters.quick === 'completed') return taskIsComplete(task, columns);
    if (filters.quick === 'cancelled') return task.status === 'cancelled';
    return task.status !== 'cancelled';
  });
}

export function listSections(tasks, columns, quick = '') {
  if (quick === 'completed') return [{ id: 'completed', label: 'Completed', tasks }];
  if (quick === 'cancelled') return [{ id: 'cancelled', label: 'Cancelled', tasks }];
  if (quick === 'waiting') return [{ id: 'waiting', label: 'Waiting', tasks }];
  if (quick === 'overdue') return [{ id: 'overdue', label: 'Overdue', tasks }];
  if (quick === 'today') return [{ id: 'today', label: 'Today', tasks }];
  if (quick === 'week') return [{ id: 'week', label: 'This week', tasks }];
  if (quick === 'upcoming') return [{ id: 'upcoming', label: 'Upcoming', tasks }];

  const today = todayIso();
  const buckets = { overdue: [], today: [], week: [], waiting: [], upcoming: [] };
  for (const task of tasks) {
    const bucket = taskBucket(task, columns, today);
    if (buckets[bucket]) buckets[bucket].push(task);
  }

  return [
    ['overdue', 'Overdue'],
    ['today', 'Today'],
    ['week', 'This week'],
    ['waiting', 'Waiting'],
    ['upcoming', 'Upcoming'],
  ]
    .map(([id, label]) => ({ id, label, tasks: buckets[id] }))
    .filter((section) => section.tasks.length);
}
